import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { BookIcon, CheckCircleIcon, LockIcon, PlayIcon, ClockIcon, ChevronDownIcon, ChevronUpIcon, StarIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useToast } from './ui/ToastContainer';
import { Tooltip } from './ui/Tooltip';
interface LearningUnit {
  id: number;
  title: {
    en: string;
    ar: string;
  };
  description: {
    en: string;
    ar: string;
  };
  lessonsCount: number;
  progress: number;
  isCompleted: boolean;
  isLocked: boolean;
  difficulty?: string;
  category?: string;
  estimatedTime?: string;
}
interface LearningPathCardProps {
  unit: LearningUnit;
}
export function LearningPathCard({
  unit
}: LearningPathCardProps) {
  const {
    isArabic
  } = useLanguage();
  const [isExpanded, setIsExpanded] = useState(false);
  const toast = useToast();
  const handleLockClick = () => {
    if (unit.isLocked) {
      toast.info(isArabic ? 'الوحدة مقفلة' : 'Unit locked', isArabic ? 'أكمل الوحدات السابقة لفتح هذه الوحدة.' : 'Complete previous units to unlock this unit.');
    }
  };
  const mockLessons = [{
    id: 1,
    title: isArabic ? 'الدرس 1: المقدمة' : 'Lesson 1: Introduction',
    duration: '10 min',
    isCompleted: unit.progress >= 20
  }, {
    id: 2,
    title: isArabic ? 'الدرس 2: المفاهيم الأساسية' : 'Lesson 2: Core Concepts',
    duration: '15 min',
    isCompleted: unit.progress >= 40
  }, {
    id: 3,
    title: isArabic ? 'الدرس 3: التطبيق العملي' : 'Lesson 3: Practical Application',
    duration: '20 min',
    isCompleted: unit.progress >= 60
  }, {
    id: 4,
    title: isArabic ? 'الدرس 4: تمارين' : 'Lesson 4: Exercises',
    duration: '15 min',
    isCompleted: unit.progress >= 80
  }, {
    id: 5,
    title: isArabic ? 'الدرس 5: اختبار قصير' : 'Lesson 5: Quiz',
    duration: '10 min',
    isCompleted: unit.progress >= 100
  }].slice(0, unit.lessonsCount > 5 ? 5 : unit.lessonsCount);
  const getDifficultyColor = () => {
    switch (unit.difficulty) {
      case 'beginner':
        return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30';
      case 'intermediate':
        return 'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/30';
      case 'advanced':
        return 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30';
      default:
        return 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30';
    }
  };
  return <div className={`
        relative bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden transition-all duration-300
        ${unit.isLocked ? 'opacity-75' : 'hover:shadow-lg'}
        ${isExpanded ? 'ring-2 ring-blue-500 dark:ring-blue-400' : ''}
      `} data-id="element-770">
      <div className="p-6" data-id="element-771">
        <div className="flex items-start justify-between gap-4" data-id="element-772">
          <div className="flex-1" data-id="element-773">
            <div className="flex items-center gap-3 mb-2" data-id="element-774">
              <div className={`
                  p-2 rounded-lg
                  ${unit.isCompleted ? 'bg-green-100 dark:bg-green-900/30' : unit.isLocked ? 'bg-gray-100 dark:bg-gray-900/30' : 'bg-blue-100 dark:bg-blue-900/30'}
                `} data-id="element-775">
                {unit.isCompleted ? <CheckCircleIcon className="w-5 h-5 text-green-600 dark:text-green-400" data-id="element-776" /> : unit.isLocked ? <LockIcon className="w-5 h-5 text-gray-400 dark:text-gray-500" data-id="element-777" /> : <BookIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-778" />}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white" data-id="element-779">
                {isArabic ? unit.title.ar : unit.title.en}
              </h3>
            </div>
            <div className="flex flex-wrap items-center gap-3 mb-3 text-sm text-gray-500 dark:text-gray-400" data-id="element-780">
              {unit.difficulty && <span className={`px-2 py-0.5 rounded-full ${getDifficultyColor()}`} data-id="element-781">
                  {unit.difficulty === 'beginner' ? isArabic ? 'مبتدئ' : 'Beginner' : unit.difficulty === 'intermediate' ? isArabic ? 'متوسط' : 'Intermediate' : isArabic ? 'متقدم' : 'Advanced'}
                </span>}
              {unit.estimatedTime && <div className="flex items-center gap-1" data-id="element-782">
                  <ClockIcon className="w-4 h-4" data-id="element-783" />
                  <span data-id="element-784">{unit.estimatedTime}</span>
                </div>}
              <div className="flex items-center gap-1" data-id="element-785">
                <BookIcon className="w-4 h-4" data-id="element-786" />
                <span data-id="element-787">
                  {unit.lessonsCount} {isArabic ? 'دروس' : 'lessons'}
                </span>
              </div>
            </div>
            <p className="text-gray-600 dark:text-gray-300 mb-4" data-id="element-788">
              {isArabic ? unit.description.ar : unit.description.en}
            </p>
            <div className="space-y-2" data-id="element-789">
              <div className="flex items-center justify-between text-sm" data-id="element-790">
                <span className="text-gray-600 dark:text-gray-400" data-id="element-791">
                  {unit.progress}% {isArabic ? 'مكتمل' : 'complete'}
                </span>
                <span className="text-gray-600 dark:text-gray-400" data-id="element-792">
                  {unit.lessonsCount} {isArabic ? 'دروس' : 'lessons'}
                </span>
              </div>
              <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-793">
                <div className={`h-full transition-all duration-300
                    ${unit.isCompleted ? 'bg-green-500 dark:bg-green-400' : 'bg-blue-600 dark:bg-blue-400'}
                  `} style={{
                width: `${unit.progress}%`
              }} data-id="element-794" />
              </div>
            </div>
          </div>
        </div>
        {isExpanded && !unit.isLocked && <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 animate-fadeIn" data-id="element-795">
            <h4 className="font-medium text-gray-900 dark:text-white mb-3" data-id="element-796">
              {isArabic ? 'محتوى الوحدة' : 'Unit Content'}
            </h4>
            <div className="space-y-2" data-id="element-797">
              {mockLessons.map(lesson => <div key={lesson.id} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-id="element-798">
                  <div className="flex items-center gap-3" data-id="element-799">
                    {lesson.isCompleted ? <CheckCircleIcon className="w-5 h-5 text-green-500 dark:text-green-400" data-id="element-800" /> : <PlayIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-801" />}
                    <span className={`${lesson.isCompleted ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-white'}`} data-id="element-802">
                      {lesson.title}
                    </span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-803">
                    <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-804">
                      {lesson.duration}
                    </span>
                    {lesson.isCompleted && <Tooltip content={isArabic ? 'مكتمل' : 'Completed'} data-id="element-805">
                        <span className="p-1 bg-green-100 dark:bg-green-900/30 rounded-full" data-id="element-806">
                          <CheckCircleIcon className="w-4 h-4 text-green-500 dark:text-green-400" data-id="element-807" />
                        </span>
                      </Tooltip>}
                  </div>
                </div>)}
            </div>
          </div>}
        <div className="mt-6 flex items-center justify-between" data-id="element-808">
          {!unit.isLocked ? <Link to={`/learn/unit/${unit.id}`} className={`
                flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg
                ${unit.isCompleted ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50' : 'bg-blue-600 text-white hover:bg-blue-700 transform hover:-translate-y-0.5'}
                transition-all
              `} data-id="element-809">
              <PlayIcon className="w-4 h-4" data-id="element-810" />
              <span data-id="element-811">
                {unit.isCompleted ? isArabic ? 'مراجعة' : 'Review' : isArabic ? 'متابعة' : 'Continue'}
              </span>
            </Link> : <button onClick={handleLockClick} disabled className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-lg cursor-not-allowed" data-id="element-812">
              <LockIcon className="w-4 h-4" data-id="element-813" />
              <span data-id="element-814">{isArabic ? 'مقفل' : 'Locked'}</span>
            </button>}
          <button onClick={() => setIsExpanded(!isExpanded)} className="ml-2 p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors" aria-label={isExpanded ? 'Collapse' : 'Expand'} disabled={unit.isLocked} data-id="element-815">
            {isExpanded ? <ChevronUpIcon className="w-5 h-5" data-id="element-816" /> : <ChevronDownIcon className="w-5 h-5" data-id="element-817" />}
          </button>
        </div>
      </div>
    </div>;
}